// Bulk template activation status cache to prevent N+1 queries
import { supabase } from '@/integrations/supabase/client';
import { requestThrottler } from './requestThrottle';

interface TemplateStatusMap {
  [templateId: string]: boolean;
}

interface CacheEntry {
  activation: TemplateStatusMap;
  publicStatus: TemplateStatusMap;
  timestamp: number;
}

class TemplateActivationCache {
  private cache: CacheEntry | null = null;
  private readonly ttl = 10 * 60 * 1000; // 10 minutes TTL

  async getTemplateStatuses(
    templateIds: string[]
  ): Promise<{
    activationMap: TemplateStatusMap;
    publicMap: TemplateStatusMap;
  }> {
    const now = Date.now();

    // Return cached value if still valid and contains all requested templates
    if (this.cache && now - this.cache.timestamp < this.ttl) {
      const hasAllTemplates = templateIds.every(
        (id) => id in this.cache!.activation
      );
      if (hasAllTemplates) {
        console.log('[TemplateActivationCache] Using cached statuses');
        return {
          activationMap: this.cache.activation,
          publicMap: this.cache.publicStatus,
        };
      }
    }

    // Use request throttler to prevent duplicate calls
    const cacheKey = `template-statuses-bulk`;

    try {
      console.log('[TemplateActivationCache] Fetching bulk statuses');

      const statuses = await requestThrottler.throttle(
        cacheKey,
        async () => {
          // Fetch all template settings in one query
          const { data, error } = await supabase
            .from('system_settings')
            .select('setting_key, setting_value, is_public')
            .like('setting_key', 'template_active_%');

          if (error) throw error;

          // Build maps - default to false if no setting exists
          const activationMap: TemplateStatusMap = {};
          const publicMap: TemplateStatusMap = {};

          // Initialize all requested templates as false (default)
          templateIds.forEach((templateId) => {
            activationMap[templateId] = false;
            publicMap[templateId] = false;
          });

          // Override with actual settings
          data?.forEach((setting) => {
            const templateId = setting.setting_key.replace(
              'template_active_',
              ''
            );
            // Only include if it's one of the requested templates
            if (templateIds.includes(templateId)) {
              activationMap[templateId] = setting.setting_value !== 'false';
              publicMap[templateId] = setting.is_public === true;
            }
          });

          return { activationMap, publicMap };
        },
        { ttl: this.ttl }
      );

      // Update cache
      this.cache = {
        activation: statuses.activationMap,
        publicStatus: statuses.publicMap,
        timestamp: now,
      };

      return statuses;
    } catch (error) {
      console.error(
        '[TemplateActivationCache] Failed to fetch statuses:',
        error
      );

      // Return cached data if available as fallback
      if (this.cache) {
        console.log(
          '[TemplateActivationCache] Returning stale cache due to error'
        );
        return {
          activationMap: this.cache.activation,
          publicMap: this.cache.publicStatus,
        };
      }

      // Last resort: default all to false
      const fallbackActivation: TemplateStatusMap = {};
      const fallbackPublic: TemplateStatusMap = {};
      templateIds.forEach((templateId) => {
        fallbackActivation[templateId] = false;
        fallbackPublic[templateId] = false;
      });
      return { activationMap: fallbackActivation, publicMap: fallbackPublic };
    }
  }

  // Clear cache when status changes
  clear() {
    console.log('[TemplateActivationCache] Clearing status cache');
    this.cache = null;
    requestThrottler.clearPattern('template-statuses');
  }

  // Get cache stats for debugging
  getStats() {
    if (!this.cache) {
      return { cached: false, age: 0, templateCount: 0 };
    }

    const age = Date.now() - this.cache.timestamp;
    const isValid = age < this.ttl;

    return {
      cached: true,
      age: Math.round(age / 1000), // seconds
      isValid,
      templateCount: Object.keys(this.cache.activation).length,
    };
  }
}

export const templateActivationCache = new TemplateActivationCache();
